import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo, faqData } from '@/mocks/movies';
import useSEO from '@/hooks/useSEO';

const submitAddr = 'https://readdy.ai/api/form/d9nit3u8mbnljin5a1gg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: '关于我们 - 人人影院',
    description: '人人影院成立于2018年，致力于为影迷提供免费高清在线观影体验。了解我们的品牌故事、平台口碑和联系方式。',
    keywords: '人人影院,关于人人影院,免费影视平台,在线观影平台,联系我们',
    canonicalUrl: 'https://renrencinema.com/about',
    ogTitle: '关于我们 - 人人影院',
    ogDescription: '人人影院成立于2018年，致力于为影迷提供免费高清在线观影体验。',
    ogUrl: 'https://renrencinema.com/about',
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "人人影院",
        "url": "https://renrencinema.com/",
        "foundingDate": "2018",
        "description": "免费高清在线影视平台",
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "contact@renrencinema.com",
          "contactType": "customer service"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "关于我们 - 人人影院",
        "description": "了解人人影院的品牌故事、平台口碑和联系方式",
        "url": "https://renrencinema.com/about",
        "isPartOf": { "@type": "WebSite", "name": "人人影院", "url": "https://renrencinema.com/" }
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://renrencinema.com/" },
          { "@type": "ListItem", "position": 2, "name": "关于我们", "item": "https://renrencinema.com/about" }
        ]
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map((faq) => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
        }))
      }
    ],
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    setFormError('');

    try {
      const formEl = e.currentTarget;
      const honeypot = formEl.querySelector<HTMLInputElement>('input[name="phone_alt"]');
      if (honeypot && honeypot.value.trim() !== '') {
        setFormStatus('success');
        return;
      }

      const formData = new FormData(formEl);
      if (honeypot) {
        formData.delete('phone_alt');
      }

      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        (parsed as Record<string, unknown>)?.meta?.message ||
        (parsed as Record<string, unknown>)?.message ||
        (parsed as Record<string, unknown>)?.meta?.detail ||
        '';

      if (
        response.ok &&
        parsed &&
        typeof parsed === 'object' &&
        (parsed as Record<string, unknown>).code === 'OK'
      ) {
        setFormStatus('success');
        formEl.reset();
      } else {
        const errorMsg =
          typeof serverMsg === 'string' && serverMsg.includes('spam')
            ? '提交失败，请稍后再试。'
            : typeof serverMsg === 'string' && serverMsg
              ? serverMsg
              : '提交失败，请稍后重试。';
        setFormError(String(errorMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-12 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
              关于人人影院
            </h1>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto leading-relaxed">
              成立于{aboutInfo.founded}，致力于{aboutInfo.mission}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center bg-background-100 rounded-xl p-5">
                  <p className="text-2xl md:text-3xl font-bold text-primary-500 font-heading mb-1">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-foreground-900 font-heading mb-4">
                  品牌使命
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  人人影院成立于{aboutInfo.founded}，是一个致力于为全球华语用户提供免费高清影视内容的在线平台。
                  我们的使命是<strong>{aboutInfo.mission}</strong>，愿景是<strong>{aboutInfo.vision}</strong>。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  经过多年发展，人人影院已成为华语地区极具影响力的在线影视平台之一。
                  我们拥有超过10万部影视资源，涵盖电影、电视剧、动漫、综艺、纪录片等多个品类，
                  每日更新内容超过百部，满足每一位影迷的观影需求。
                </p>
                <h3 className="text-base font-bold text-foreground-900 mb-3">核心价值观</h3>
                <div className="flex flex-wrap gap-2">
                  {aboutInfo.values.map((v) => (
                    <span
                      key={v}
                      className="px-3 py-1.5 bg-accent-100 text-accent-800 text-xs font-medium rounded-full whitespace-nowrap"
                    >
                      {v}
                    </span>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-xl p-6">
                <h3 className="text-base font-bold text-foreground-900 mb-4">联系我们</h3>
                <form
                  id="about-contact-form"
                  data-readdy-form=""
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="form-name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      id="form-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      id="form-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="form-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500字符</p>
                  </div>
                  <input
                    name="phone_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="website-alt-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200"
                  >
                    {formStatus === 'sending' ? '提交中...' : '提交留言'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">提交成功！感谢您的留言，我们会尽快回复。</p>
                  )}
                  {formStatus === 'error' && formError && (
                    <p className="text-sm text-red-600 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg md:text-2xl font-bold text-foreground-900 font-heading mb-6 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {aboutInfo.testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-xl p-5">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="w-3.5 h-3.5 inline-flex items-center justify-center">
                        <i
                          className={`text-xs ${
                            i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'
                          }`}
                        ></i>
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-lg md:text-2xl font-bold text-foreground-900 font-heading mb-6 text-center">
              常见问题
            </h2>
            <div className="max-w-2xl mx-auto space-y-3">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-background-50 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100/70 transition-colors duration-200"
                  >
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <span className={`w-5 h-5 shrink-0 inline-flex items-center justify-center transition-transform duration-200 ${openFaq === index ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </span>
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}